import { tableAnatomy as parts } from "@chakra-ui/anatomy"
import type { StyleFunctionProps } from "@chakra-ui/styled-system"
import { createMultiStyleConfigHelpers } from "@chakra-ui/styled-system"
import { theme } from "@chakra-ui/theme"

// Enable Chakra UI Table to accept custom props
declare module "@chakra-ui/react" {
  interface TableProps {
    disableRowHover?: boolean
  }
}

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(parts.keys)

const variantSimple = definePartsStyle((props: StyleFunctionProps) => {
  const defaultStyles = theme.components.Table.variants?.simple?.(props) ?? {}

  return {
    ...defaultStyles,
    table: {
      fontSize: 14,
    },
    th: {
      px: 1.5,
      py: 3,
      textTransform: "none",
      fontSize: 12,
      fontWeight: 400,
      letterSpacing: 0,
      color: "neutrals.900",
      borderBottom: "1px solid",
      borderColor: "neutrals.400",
    },
    td: {
      px: 1.5,
      py: 1,
      lineHeight: 1.71,
      border: "1px solid var(--chakra-colors-neutrals-200)",
      "&:first-child": {
        borderLeft: "none",
      },
      "&:last-child": {
        borderRight: "none",
      },
    },
    caption: {
      my: 3,
      fontSize: 12,
      fontWeight: 400,
      color: "neutrals.600",
    },
  }
})

const variantSimpleRows = definePartsStyle((props: StyleFunctionProps) => {
  const variantSimpleStyles = variantSimple(props)
  const { disableRowHover } = props

  return {
    ...variantSimpleStyles,
    tr: {
      ...(!disableRowHover && {
        "&:not(:where(thead tr))": {
          _hover: {
            background: "neutrals.100",
          },
        },
      }),
    },
    td: {
      ...variantSimpleStyles.td,
      borderLeft: "none",
      borderRight: "none",
    },
  }
})

const variantRows = definePartsStyle((_props: StyleFunctionProps) => {
  return {
    table: {
      borderCollapse: "separate",
      borderSpacing: "0 12px",
      fontSize: "14px",
      lineHeight: "26px",
    },
    th: {
      textTransform: "none",
      fontWeight: 600,
      border: "none",
    },
    thead: {
      textTransform: "none",
      border: "none",
    },
    tr: {
      bg: "#ffffff",
      boxShadow: "0px 0px 30px rgba(0, 0, 0, 0.07)",
      borderRadius: "15px",
    },
    td: {
      _first: {
        borderTopLeftRadius: "15px",
        borderBottomLeftRadius: "15px",
      },
      _last: {
        borderTopRightRadius: "15px",
        borderBottomRightRadius: "15px",
      },
    },
  }
})

/**
 * @see {@link https://github.com/chakra-ui/chakra-ui/blob/v2/packages/theme/src/components/table.ts}
 */
export const Table = defineMultiStyleConfig({
  baseStyle: {
    table: {
      borderCollapse: "collapse",
    },
    caption: {
      whiteSpace: "wrap",
    },
  },
  variants: {
    simple: variantSimple,
    simpleRows: variantSimpleRows,
    rows: variantRows,
  },
  defaultProps: {
    variant: "rows",
  },
})
